import PageContainer from '@/components/PageContainer';
import { get } from '@/utils/queries';
import { Client } from '@types';
import { DashboardWrapper } from '@/components/DashboardWrapper';
import { IUserContext, useUserContext } from '@/context/UserContext';
import ReactMarkdown from 'react-markdown';
import { markdownInlineComponents } from '@/pages/FAQ/helper';
import { Spinner } from '@/components/Spinner';

type Paragraph = {
  type: 'paragraph';
  text: string;
};
type BulletList = {
  type: 'list';
  items: string[];
};
type FAQData = {
  id?: number;
  question: string;
  answer: (Paragraph | BulletList)[];
  created_at?: string;
  updated_at?: string;
};

// Función para limpiar markdown y dejar solo inline (sin saltos de línea ni encabezados)
function toInlineMarkdown(md: string) {
  // Elimina saltos de línea y encabezados
  return md
    .replace(/^#+\s?/gm, '') // elimina encabezados tipo #, ##, etc.
    .replace(/\n/g, ' ') // reemplaza saltos de línea por espacio
    .trim();
}

export default function FAQPage() {
  const faqDataAPIUrl = '/faqData';
  const { companyName, auth }: IUserContext = useUserContext();

  const { data: companyData } = auth?.isClient
    ? get<Client>(`account/${auth.clients[0].id}`)
    : get<Client[]>('account/list');

  const {
    data: faqs,
    isValidating: isLoading,
    error,
  } = get<FAQData[]>(`${faqDataAPIUrl}`);

  const props = {
    ...(companyName && { companyName }),
    ...[companyData && { companyData }],
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className='p-8 text-center text-error'>Error while loading FAQs</div>
    );
  }
  if (!faqs || faqs.length === 0) {
    return <div className='p-8 text-center'>No FAQs avaialble.</div>;
  }

  return (
    <PageContainer name='faq'>
      <DashboardWrapper {...props}>
        <div className=' mx-auto mt-8 w-[80%]'>
          <h1 className='text-3xl font-bold mb-6 text-center '>FAQ</h1>
          <h1 className='mb-6'>
            Welcome to our FAQ hub—your go-to resource for all things finance.
            Whether you’re curious about our services, pricing, or how we keep
            your books in tip-top shape, you’ll find clear answers right here.
          </h1>
          <div className='space-y-4'>
            {faqs.map((faq: FAQData) => (
              <details
                key={faq.id}
                className='bg-base-200 rounded-lg shadow p-4'
              >
                <summary className='cursor-pointer text-lg font-semibold select-none'>
                  <ReactMarkdown
                    // Overwrites how this tags are rendered in markdown}
                    components={markdownInlineComponents}
                  >
                    {toInlineMarkdown(faq.question)}
                  </ReactMarkdown>
                </summary>
                <div className='mt-3 space-y-2'>
                  {faq.answer.map((ans: Paragraph | BulletList, i: number) => {
                    if (ans.type === 'paragraph') {
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <ReactMarkdown key={`markdown${i}`}>
                          {ans.text}
                        </ReactMarkdown>
                      );
                    }
                    if (ans.type === 'list') {
                      return (
                        // eslint-disable-next-line react/no-array-index-key
                        <ul key={`ul${i}`} className='list-disc ml-6'>
                          {ans.items.map((item: string, j: number) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <li key={`li${j}`}>
                              <ReactMarkdown>{item}</ReactMarkdown>
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    return null;
                  })}
                </div>
              </details>
            ))}
          </div>
        </div>
      </DashboardWrapper>
    </PageContainer>
  );
}
export { FAQPage };
